import Api from '../../../services/Api';
import SiteMapModel from '../../Sitemap/models/SiteMapModel';
import WizardSessionData from '../../../services/WizardSessionData';
import UserPreviewDataModel from '../model/user-preview-data/UserPreviewDataModel';
import SiteBuilderModel from '../model/SiteBuilderModel';
import SocialMediaModel from '../../SocialMedia/models/SocialMediaModel';

export interface PageBuilderData {
  id: string | number;
  pageName: string;
  description: string;
  pagePath: string;
  isCustomPage: boolean;
  keywords: string[];
  type: string;
  pictures: {
    areaName: string; // area name
    url: string;
  }[];
  section: {
    name: string;
    variant: number;
  }[];
  areas: {
    name: string; // area name
    text: string;
  }[];
  navigations: {
    name: string;
    version: number;
  }[];
  children: PageBuilderData[];
}

/**
 * This class is responsible for creating a website preview.
 * It collects all the data from the wizard and the page builder and sends it to the backend where the website is generated.
 */
class WebsitePreviewCreator {
  public collectBusinessData() {
    let businessData = WizardSessionData.getWizardData();
    businessData = { ...businessData, ...SocialMediaModel.getWizardData() };
    delete businessData.siteMap;
    return businessData;
  }

  /**
   *
   * Iterate over the site map and collect all the data for the pages.
   * Data includes the generated content by the AI and the user modified content and pictures, sections, navigations.
   *
   * @param wizardData
   * @param generatedTemplateData
   * @param userModifiedSiteData
   * @returns
   */
  private collectPageSiteMapData(wizardData) {
    let siteMapData: PageBuilderData[] = [];
    let pageGeneratedData = SiteBuilderModel.generatedPages;

    let recur = (pageId: string | number): PageBuilderData => {
      let pageData = wizardData.siteMap.get(pageId);

      let page: PageBuilderData = {
        id: pageId,
        pageName: pageData.pageName,
        pagePath: pageData.pagePath,
        keywords: pageData.keywords,
        description: pageData.description,
        isCustomPage: pageData.isCustomPage,
        type: pageData.type,

        // content
        pictures: [],
        section: [],
        areas: [],
        navigations: [],

        children: []
      };

      // for areas we should first set the generated content by the AI for the current page
      // !IMPORTANT for sections, navigations we don't have generated content by the AI
      // pageGeneratedData.sitemap contains the generated content by the AI for the current page
      let generatedPageContent = pageGeneratedData.find((page) => {
        return page.getId() === pageId;
      });

      if (generatedPageContent !== undefined) {
        page.areas = generatedPageContent.getTextAreas().map((area) => {
          return {
            name: area.name,
            text: area.text
          };
        });

        page.pictures = generatedPageContent
          .getPictureAreas()
          .filter((area) => area.image !== null)
          .map((area) => {
            return {
              areaName: area.name,
              url: area.image.PresignedUrl
            };
          });
      }

      const userModifiedPageElements =
        UserPreviewDataModel.pageElements[pageId];

      if (userModifiedPageElements) {
        userModifiedPageElements.forEach((element) => {
          element.injectSessionData(page);
        });
      }

      const children = [];

      for (let [childId, childData] of wizardData.siteMap) {
        if (childData.parent === pageId) {
          children.push(childId);
        }
      }

      for (let childId of children) {
        page.children.push(recur(childId));
      }

      return page;
    };

    // [1] iterate over all pages from the sitemap
    for (let [pageId, pageData] of wizardData.siteMap) {
      if (pageData.parent === SiteMapModel.SITEMAP_ROOT_INTERNAL) {
        siteMapData.push(recur(pageId));
      }
    }

    return siteMapData;
  }

  /**
   *
   * @param generatedTemplateData
   * @param userModifiedSiteData
   */
  private collectTemplateData() {
    const templateGeneratedData = SiteBuilderModel.templateGeneratedData;

    let templateData = {
      pictures: [],
      section: [],
      areas: [],
      navigations: []
    };

    // push generated text areas
    templateGeneratedData.getTemplateAreas().forEach((area) => {
      templateData.areas.push({
        name: area.name,
        text: area.text
      });
    });

    // push generated images
    templateGeneratedData.getTemplateImages().forEach((image) => {
      if (image.image === null) {
        return;
      }

      templateData.pictures.push({
        areaName: image.name,
        url: image.image.PresignedUrl
      });
    });

    const userModifiedTemplateData = UserPreviewDataModel.templateElements;

    if (userModifiedTemplateData.length) {
      userModifiedTemplateData.forEach((templateElement) => {
        templateElement.injectSessionData(templateData);
      });
    }

    return templateData;
  }

  async goToPurchase() {
    // template data generated by the AI
    const wizardData = WizardSessionData.getWizardData();
    
    const websitePageMapData = this.collectPageSiteMapData(wizardData);
    const templateData = this.collectTemplateData();

    const siteFinalData = {
      ...this.collectBusinessData(),
      sitemap: websitePageMapData,
      templates_data: templateData
    };

    //save session data in backend. Then, the user will be redirected to the purchase page
    let result = await Api.saveSessionData(siteFinalData);

    return result;
  }
}

export default new WebsitePreviewCreator();
